"use client";
import React, { CSSProperties, useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { DotButton, useDotButton } from "./CarouselDots";
import { EmblaCarouselType, EmblaOptionsType } from "embla-carousel";
import SectionTitle, { ISectionTitleProps } from "../Title/SectionTitle";
import BaseCard from "../Cards/BaseCard";
import { cn } from "@pulsex/utils";
import "@/styles/carousel-base.css";
import { Post, Widget } from "@pulsex/types";
import CarouselArrows from "./CarouselArrows";

type IBaseCarousel = ISectionTitleProps & {
    posts: Array<Post>;
    renderItem?: (
        slide: any,
        index: number,
        emblaApi?: EmblaCarouselType
    ) => React.ReactNode;
    cropSize?: string;
    navigationDots?: string;
    options?: EmblaOptionsType;
    slidesPerView?: number;
    className?: string;
    moreLink?: string;
    showTitle?: boolean;
    showDate?: boolean;
    author?: boolean;
    inlineCategory?: boolean;
    containerClassName?: string;
    viewportClassName?: string
    widget?: Widget
    showCategory?: boolean
    resolution?: number
};

const BaseCarousel: React.FC<IBaseCarousel> = ({
    posts,
    options = {
        align: "start",
        dragFree: false,
        slidesToScroll: "auto",
    },
    renderItem,
    cropSize = "rectangle",
    showTitle,
    showDate,
    inlineCategory,
    moreLink,
    navigationDots,
    slidesPerView = 4,
    author,
    className,
    containerClassName,
    viewportClassName,
    widget,
    showCategory = true,
    resolution = 3,
    ...rest
}) => {
    const [emblaRef, emblaApi] = useEmblaCarousel(options);
    const [windowWidth, setWindowWidth] = useState<number>(0)

    const { selectedIndex, scrollSnaps, onDotButtonClick } =
        useDotButton(emblaApi);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            const handleResize = () => {
                setWindowWidth(window.innerWidth)
            };

            window.addEventListener('resize', handleResize);
            handleResize();

            return () => {
                window.removeEventListener('resize', handleResize);
            };
        }
    }, []);

    if (!posts) return;
    const backgroundImage = (rest as any).backgroundImage;

    return (
        <div className={backgroundImage && "relative py-10 lg:my-5 lg:py-1"}>
            <section
                className={cn(
                    "container my-5",
                    navigationDots === "bottom" && "pb-10",
                    backgroundImage && "z-10 relative",
                    className
                )}
            >
                <div className="flex items-center justify-between mb-5 lg:mb-3">
                    <SectionTitle
                        title={(rest as any).title}
                        className="w-fit mb-0 lg:mb-0"
                        more={moreLink}
                        widget={widget}
                    />
                    {navigationDots?.includes('top') && (
                        <div className="md:hidden flex gap-2 items-center select-none">
                            {scrollSnaps.map((_, index) => (
                                <DotButton
                                    key={index}
                                    onClick={() => onDotButtonClick(index)}
                                    className={cn(
                                        "rounded w-6 h-6 p-1 flex items-center justify-center bg-gray-400 text-white text-md font-medium opacity-60",
                                        selectedIndex === index &&
                                            "bg-primary opacity-100"
                                    )}
                                >
                                    {index + 1}
                                </DotButton>
                            ))}
                        </div>
                    )}
                </div>
                <div
                    className="base-carousel embla relative"
                    style={
                        {
                            "--slide-size": `${slidesPerView === 1 ? '100%' : windowWidth < 768 ? "70%" : `calc(${100 / slidesPerView}% - 0.8rem)`}`,
                        } as CSSProperties
                    }
                >
                    <div className={cn("embla__viewport p-1", viewportClassName)} ref={emblaRef}>
                        <div className={cn("embla__container relative", containerClassName)}>
                            {posts.map((slide, index) => {
                                if (renderItem)
                                    return (
                                        <div
                                            className="embla__slide"
                                            key={index}
                                        >
                                            {renderItem(slide, index, emblaApi)}
                                        </div>
                                    );
                                return (
                                    <div className="embla__slide " key={index}>
                                        <BaseCard
                                            showTitle={showTitle}
                                            showDate={showDate}
                                            showCategory={showCategory}
                                            post={slide}
                                            resolution={resolution}
                                            key={index}
                                            cropSize={cropSize}
                                            inlineCategory={inlineCategory}
                                            variant="opened"
                                            className="transition-all duration-300  hover:scale-[1.02] h-full"
                                            titleClassName="text-xl"
                                            author={author}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* side arrows */}
                    {navigationDots?.includes('sides') && posts.length > 1 && <CarouselArrows emblaApi={emblaApi as EmblaCarouselType} />}

                    {/* bottom navigation dots */}
                    {navigationDots?.includes('bottom') && (
                        <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 flex gap-4 items-center">
                            {scrollSnaps.map((_, index) => (
                                <div
                                    key={index}
                                    onClick={() => onDotButtonClick(index)}
                                    className={cn(
                                        "w-3 h-3 rounded-full bg-white opacity-60 transition-all cursor-pointer mb-3",
                                        selectedIndex === index && "opacity-100"
                                    )}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </section>
            {backgroundImage && (
                <>
                    <img
                        src={backgroundImage}
                        alt={"background decoration"}
                        className="absolute z-0 inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute z-[1] inset-0 w-full h-full bg-blue opacity-[78%]" />
                </>
            )}
        </div>
    );
};

export default BaseCarousel;
